import moment from 'moment';
import { ServerTimeType } from '../types';
import { defaultFilterValue } from '../constants';
import { has } from 'lodash-es';
import { channelSplitFlag } from '../constants/channel';
import { DBO_TM } from '@op/utils/format';

type FilterStorageGameInfo = {
  edition_unified_id: string;
  filterInstanceId?: string;
};

const getGameInfoKey = (gameInfo: FilterStorageGameInfo) => {
  const gameId = gameInfo?.edition_unified_id;
  if (!gameId) {
    return '';
  }
  const { filterInstanceId } = gameInfo;
  return filterInstanceId ? `${gameId}__${filterInstanceId}` : gameId;
};

/**
 * 获取cond接口缓存数据的key
 * @param gameInfo
 * @returns
 */
export const getCondStorageKey = (gameInfo: { edition_unified_id: string }) => {
  return `${getGameInfoKey(gameInfo)}-cond-filter`;
};

/**
 * 获取filter cond值
 * @param gameInfo
 * @returns
 */
export const getFilterCondStorage = (gameInfo: {
  edition_unified_id: string;
}) => {
  const key = getCondStorageKey(gameInfo);

  try {
    return JSON.parse((window.sessionStorage as any).getItem(key));
  } catch (e) {
    return null;
  }
};

/**
 * 保存filter cond值
 * @param gameInfo
 * @param filterData
 */
export const saveFilterCondStorage = (gameInfo: any, filterData: any) => {
  try {
    const key = getCondStorageKey(gameInfo);
    sessionStorage.setItem(key, JSON.stringify(filterData));
  } catch (error) {}
};

/**
 * 修改filter cond值
 * @param gameInfo
 * @param filterData
 * @returns
 */
export const updateFilterCondStorage = (gameInfo: any, filterData: any) => {
  if (!filterData) return;
  const value = getFilterCondStorage(gameInfo);
  const newValue = { ...value, ...filterData };

  saveFilterCondStorage(gameInfo, newValue);
};

export const removeFilterCondStorage = (gameInfo: {
  edition_unified_id: string;
}) => {
  const key = getCondStorageKey(gameInfo);
  sessionStorage.removeItem(key);
};

/**
 * 获取filter name 缓存的key
 * @param gameInfo
 * @returns
 */
export const getFilterNameStorageKey = (gameInfo: {
  edition_unified_id: string;
}) => {
  return `${getGameInfoKey(gameInfo)}-filter-name`;
};

/**
 * 获取filter name值
 * @param gameInfo
 * @returns
 */
export const getFilterName = (gameInfo: { edition_unified_id: string }) => {
  const key = getFilterNameStorageKey(gameInfo);
  return sessionStorage.getItem(key);
};

/**
 * 保存filter name方法
 * @param gameInfo 游戏信息
 * @param value    数据，如果是undefined，则删除缓存
 * @param infoKey  信息key，可以作为检查的信息
 * @returns
 */
export const saveFilterName = (
  gameInfo: { edition_unified_id: string },
  value?: string,
  infoKey?: string,
) => {
  const key = getFilterNameStorageKey(gameInfo);
  if (value) {
    sessionStorage.setItem(key, value);
  } else {
    // console.log(infoKey, '删除filter Name的位置');
    sessionStorage.removeItem(key);
  }
};

/**
 * 获取filter 值的key
 * @param gameInfo
 * @returns
 */
export const getFilterValueStorageKey = (gameInfo: {
  edition_unified_id: string;
}) => {
  return `${getGameInfoKey(gameInfo)}-filter-value`;
};

/**
 * 获取filter 值
 * @param gameInfo
 * @returns
 */
export const getFilterValueStorage = (gameInfo: {
  edition_unified_id: string;
}) => {
  const key = getFilterValueStorageKey(gameInfo);

  try {
    return JSON.parse((window.sessionStorage as any).getItem(key));
  } catch (e) {
    return null;
  }
};

/**
 * 将三种渠道数据合并为一种渠道数据，暂时不用
 * @param filter
 * @returns
 */
export const dealwithChannelToBack = (filter: any) => {
  let { channel = [], channel_type } = filter || {};
  let result: { [key: string]: Array<string> } = {};
  function dealwith(value: Array<string>, curKey: string) {
    if (value.length === 1 && value[0] === '-99999') return value;

    return value
      ?.filter((item: string) => item && !item.includes('99'))
      .map((key: string) => {
        if (key.includes(curKey + channelSplitFlag)) {
          return key;
        }
        return curKey + channelSplitFlag + key;
      });
  }

  if (
    channel_type &&
    channel.length > 0 &&
    ['social', 'comments', 'news', 'securitys'].includes(channel_type)
  ) {
    result['channel_' + channel_type] = dealwith(channel, channel_type);
  } else {
    result = ['comments', 'news', 'social', 'securitys'].reduce(
      (pre: any, cur: string) => {
        let filterKey = 'channel_' + cur;
        let value = filter?.[filterKey];
        if (value) {
          return {
            ...pre,
            [filterKey]: dealwith(
              value,
              cur === 'securitys' ? 'security' : cur,
            ),
          };
        }
        return pre;
      },
      {},
    );
  }
  return result;
};

export const dealwithSessionData = (data: any) => {
  if (!data) return { data: {}, isToday: true };

  const { start_time, end_time, ...otherInfo } = data || {};
  let date_type: ServerTimeType = data.date_type || 'daily';
  // 默认值
  let isToday = true;

  let { start_time: startTime, end_time: endTime } =
    DBO_TM.filterToday(date_type);
  // 如果缓存存在就使用缓存数据
  if (start_time && end_time) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    (startTime = start_time), (endTime = end_time), (date_type = date_type);
    isToday =
      moment(startTime).format('YYYY-MM-DD') ===
        moment(endTime).format('YYYY-MM-DD') &&
      moment(startTime).format('YYYY-MM-DD') ===
        moment().format('YYYY-MM-DD') &&
      date_type === 'daily';
  }

  return {
    data: {
      ...otherInfo,
      start_time: startTime,
      end_time: endTime,
    },
    isToday,
  };
};

/**
 * channel处理一下，保证格式上的一致性
 * @param value
 * @returns
 */
export function dealwithChannelToCommon(value: any) {
  const { channel_type, channel, ...otherInfo } = value;
  if (channel_type) {
    otherInfo[`channel_${channel_type}`] = channel;
  }

  return otherInfo;
}

function dealwithReviews(data: any, hasReviews = false) {
  if (hasReviews) {
    const { reviewers, exclude_reviewers, ...otherInfo } = data || {};
    return otherInfo;
  }

  return data;
}

export const setSessionStorage = (
  value: any,
  gameInfo: { edition_unified_id: string },
  noUseCache = false,
) => {
  const key = getFilterValueStorageKey(gameInfo);
  let data = defaultFilterValue();
  if (!noUseCache) {
    data = JSON.parse(sessionStorage.getItem(key) || '{}');
  }

  const otherInfo = dealwithChannelToCommon(value);
  const otherData = dealwithReviews(
    data,
    has(otherInfo, 'reviewers') || has(otherInfo, 'exclude_reviewers'),
  );

  const newCache = Object.assign(otherData, otherInfo);
  sessionStorage.setItem(key, JSON.stringify(newCache));
};

const sessionStorageKey = 'genre_game_map_key';
export function operateGenreStorage(value?: any) {
  if (value) {
    return sessionStorage.setItem(sessionStorageKey, JSON.stringify(value));
  }
  try {
    const genreValue = sessionStorage.getItem(sessionStorageKey);
    return JSON.parse(genreValue || `{'en-US': {}, 'zh-CN': {}}`);
  } catch (error) {
    return { 'en-US': {}, 'zh-CN': {} };
  }
}

export function transformGenreGame(lang: 'en-US' | 'zh-CN' = 'en-US') {
  const genreMap = operateGenreStorage();
  const langGenreMap = genreMap?.[lang] || {};
  return function (key: string) {
    const keyText = key?.toLocaleLowerCase?.().split('|');
    const realKeyText = key?.split('|');
    const genreGameText = keyText
      ?.map(
        (key: string, index: number) =>
          langGenreMap[key] || realKeyText?.[index],
      )
      .join(' | ');

    return genreGameText;
  };
}
